import { useCallback, useEffect, useState } from 'react';
import { describeError } from '../api/client';

export interface AsyncState<T> {
  readonly data: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  reload(): void;
}

/** Loads data when deps change; exposes reload() for after mutations. */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  const [tick, setTick] = useState(0);

  const stableLoad = useCallback(load, deps);

  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    stableLoad().then(
      (data) => active && setState({ data, error: null, loading: false }),
      (error: unknown) =>
        active && setState({ data: null, error: describeError(error), loading: false }),
    );
    return () => {
      active = false;
    };
  }, [stableLoad, tick]);

  return { ...state, reload: () => setTick((t) => t + 1) };
}
